import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";

const BirthdayDisplay = ({ character }) => {
  // State
  const [isVisible, setIsVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  // Default color fallback
  const defaultColors = {
    primary: "#FF4D4D",
    secondary: "#6A00F4",
    accent: "#00C896"
  };

  // Memoized values
  const colors = useMemo(() => 
    character?.name ? getColorGradient(character.name) : defaultColors,
    [character]
  );

  const primaryAppearance = useMemo(() => 
    character ? getPrimaryAppearance(character) : null,
    [character]
  );

  const formattedDate = useMemo(() => 
    new Date().toLocaleDateString("en-US", { month: "long", day: "numeric" }),
    []
  );

  // Event handlers
  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({
      x: (e.clientX - rect.left) / rect.width - 0.5,
      y: (e.clientY - rect.top) / rect.height - 0.5
    });
  }, []);

  // Effects
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  if (!character) return null;

  // Transform styles
  const transformStyles = {
    card: {
      transform: `rotateY(${hovered ? mousePosition.x * 10 : 0}deg) rotateX(${hovered ? -mousePosition.y * 10 : 0}deg)`,
      transition: hovered ? "transform 0.1s ease-out" : "transform 0.5s ease-out"
    },
    image: {
      transform: hovered 
        ? `translateZ(20px) translateX(${mousePosition.x * -5}px) translateY(${mousePosition.y * -5}px)`
        : "none",
      transition: hovered ? "transform 0.2s ease-out" : "transform 0.5s ease-out"
    },
    content: {
      transform: hovered 
        ? `translateZ(10px) translateX(${mousePosition.x * 10}px) translateY(${mousePosition.y * 10}px)`
        : "none",
      transition: hovered ? "transform 0.2s ease-out" : "transform 0.5s ease-out"
    }
  };

  return (
    <div className="my-6 perspective-1000">
      <div
        ref={cardRef}
        className={`relative transform transition-all duration-700 ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
        }`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseMove={handleMouseMove}
        style={transformStyles.card}
      >
        <div className="relative overflow-hidden rounded-xl group transition-all duration-300 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.3)]">
          {/* Background */}
          <div
            className="absolute inset-0 z-10 transition-all duration-700"
            style={{
              background: `
                radial-gradient(circle at 20% 30%, ${colors.primary}33, transparent 70%),
                radial-gradient(circle at 80% 20%, ${colors.secondary}33, transparent 50%),
                radial-gradient(circle at 50% 80%, ${colors.accent}33, transparent 70%),
                linear-gradient(135deg, #0f0920 0%, #2a1458 100%)
              `,
              opacity: hovered ? 0.9 : 0.8,
            }}
          />
          
          {/* Animated patterns */}
          <div className="absolute inset-0 z-5 opacity-30">
            <div
              className={`absolute w-72 h-72 rounded-full transition-all duration-1000 ease-in-out ${
                hovered ? "scale-110" : "scale-100"
              }`}
              style={{
                background: `radial-gradient(circle, ${colors.primary}22, transparent 70%)`,
                top: "-20%",
                right: "-10%",
                transform: hovered
                  ? `translateX(${mousePosition.x * 15}px) translateY(${mousePosition.y * 15}px)`
                  : "none",
              }}
            />
            <div
              className={`absolute w-96 h-96 rounded-full transition-all duration-1500 ease-in-out ${
                hovered ? "scale-110" : "scale-100"
              }`}
              style={{
                background: `radial-gradient(circle, ${colors.secondary}22, transparent 70%)`,
                bottom: "-30%",
                left: "-10%",
                transform: hovered
                  ? `translateX(${mousePosition.x * -20}px) translateY(${mousePosition.y * -20}px)`
                  : "none",
              }}
            />
          </div>

          {/* Spotlight */}
          <div
  className={`absolute rounded-full w-56 h-56 blur-3xl transition-opacity duration-700 transform ${
    hovered ? "opacity-60" : "opacity-0"
  }`}
  style={{
    left: `${mousePosition.x * 100 + 50}%`,
    top: `${mousePosition.y * 100 + 50}%`,
    transform: "translate(-50%, -50%)",
    backgroundColor: `var(--spotlight)`,
    opacity: hovered ? 0.2 : 0 // adjust opacity here
  }}
/>

          {/* Content */}
          <div className="relative z-20 py-6 px-8 flex flex-col sm:flex-row items-center">
            {/* Character image */}
            <div
              className="relative w-36 h-48 mb-6 sm:mb-0 sm:mr-6 overflow-hidden rounded-lg shadow-xl transform transition-all duration-500 ease-out hover:scale-105"
              style={transformStyles.image}
            >
              <div
                className="absolute -inset-1 rounded-lg opacity-70 blur-sm"
                style={{
                  background: `linear-gradient(45deg, ${colors.primary}, ${colors.secondary})`,
                }}
              />
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={character.images?.webp?.imageUrl ||
                    character.images?.webp?.image_url ||
                    character.images?.jpg?.imageUrl ||
                    character.images?.jpg?.image_url || "/default-character.jpg"}
                  alt={character.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "/default-character.jpg";
                  }}
                />
              </div>
              <div
                className="absolute top-0 right-0 text-white text-xs font-bold py-1 px-2 rounded-bl-md shadow-md animate-pulse"
                style={{
                  background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                }}
              >
                BIRTHDAY
              </div>
            </div>

            {/* Character info */}
            <div className="flex-1" style={transformStyles.content}>
              {primaryAppearance && (
                <div
                  className={`text-sm font-medium mb-2 transition-all duration-700 delay-300 ${
                    isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                  }`}
                  style={{
                    color: `hsl(${colors.primary.match(/\d+/)[0]}, 100%, 80%)`,
                  }}
                >
                  {primaryAppearance}
                </div>
              )}

              <h2
                className={`text-3xl md:text-4xl font-extrabold text-var(--text-primary) mb-3 transition-all duration-700 delay-500 ${
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                }`}
                style={{ textShadow: `0 0 15px ${colors.primary}99` }}
              >
                {character.name}
              </h2>

              {character.name_kanji && (
                <div
                  className={`text-var(--text-primary) text-sm font-mono mb-4 tracking-wide transition-all duration-700 delay-700 ${
                    isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                  }`}
                >
                  {character.name_kanji}
                </div>
              )}

              {/* Stats */}
              <div
                className={`flex space-x-6 mb-5 transition-all duration-700 delay-900 ${
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                }`}
                style={{
                  transform: hovered ? "translateZ(15px)" : "none",
                }}
              >
                <div className="text-center group">
                  <div
                    className="text-lg font-bold text-var(--text-primary) group-hover:text-var(--text-primary) transition-colors duration-300"
                    style={{ textShadow: `0 0 10px ${colors.primary}99` }}
                  >
                    {formatNumber(character.favorites)}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-var(--text-primary)/80 group-hover:text-var(--text-primary) transition-colors duration-300">
                    Favorites
                  </div>
                </div>

                <div className="text-center group">
                  <div
                    className="text-lg font-bold text-var(--text-primary) group-hover:text-var(--text-primary) transition-colors duration-300"
                    style={{ textShadow: `0 0 10px ${colors.secondary}99` }}
                  >
                    {character.anime?.length || 0}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-var(--text-primary)/80 group-hover:text-var(--text-primary) transition-colors duration-300">
                    Anime
                  </div>
                </div>

                <div className="text-center group">
                  <div
                    className="text-lg font-bold text-var(--text-primary) group-hover:text-var(--text-primary) transition-colors duration-300"
                    style={{ textShadow: `0 0 10px ${colors.accent}99` }}
                  >
                    {character.manga?.length || 0}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-var(--text-primary)/80 group-hover:text-var(--text-primary) transition-colors duration-300">
                    Manga
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div
                className={`transition-all duration-700 delay-1000 ${
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                }`}
                style={{
                  transform: hovered ? "translateZ(30px)" : "none",
                }}
              >
                <Link
                  to={`/characters/${character.id}`}
                  className="inline-block relative overflow-hidden px-6 py-3 bg-transparent border-2 text-var(--text-primary) rounded-md group hover:text-var(--text-primary) transition-all duration-300"
                  style={{ borderColor: colors.primary }}
                >
                  <span
                    className="absolute inset-0 w-full h-0 transition-all duration-500 ease-out group-hover:h-full"
                    style={{
                      background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                      opacity: 0.8
                    }}
                  />
                  <span className="relative font-medium group-hover:text-white">
                    <span className="mr-2">Discover More</span>
                    <span className="inline-block transform group-hover:translate-x-1 transition-transform duration-300">
                      →
                    </span>
                  </span>
                </Link>
              </div>
            </div>
          </div>

          {/* Date */}
          <div className="absolute bottom-2 right-4 text-var(--text-primary) text-sm z-20 flex items-center">
            <span className="text-lg mr-2">🎂</span>
            <span>{formattedDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
function getPrimaryAppearance(character) {
  if (!character) return null;

  if (character.anime?.length > 0) {
    const animeAppearance = character.anime[0];
    if (animeAppearance.anime) {
      return animeAppearance.anime.title_english || animeAppearance.anime.title;
    }
  }

  if (character.manga?.length > 0) {
    const mangaAppearance = character.manga[0];
    if (mangaAppearance.manga) {
      return mangaAppearance.manga.title_english || mangaAppearance.manga.title;
    }
  }

  return null;
}

function formatNumber(num) {
  if (!num) return "0";
  if (num >= 10000) return `${(num / 1000).toFixed(1)}k`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num.toLocaleString();
}

function getColorGradient(name) {
  if (!name) return {
    primary: "#FF4D4D",
    secondary: "#6A00F4",
    accent: "#00C896"
  };

  const hash = name.split("").reduce((hash, char) => {
    return (hash << 5) - hash + char.charCodeAt(0);
  }, 0);

  const hue1 = Math.abs(hash % 360);
  const hue2 = (hue1 + 40) % 360;
  const hue3 = (hue1 + 200) % 360;

  return {
    primary: `hsl(${hue1}, 70%, 40%)`,
    secondary: `hsl(${hue2}, 80%, 35%)`,
    accent: `hsl(${hue3}, 70%, 45%)`,
  };
}

// CSS injection
const additionalStyles = `
<style>
  .perspective-1000 {
    perspective: 1000px;
  }
</style>
`;

if (typeof document !== "undefined") {
  const styleElement = document.createElement("div");
  styleElement.innerHTML = additionalStyles;
  document.head.appendChild(styleElement.firstChild);
}

export default BirthdayDisplay;